import { css } from '@emotion/react';
import { FC, ReactNode, useMemo } from 'react';
import OriginalReactMarkdown from 'react-markdown';

import { ChakraTheme } from '@/styles/themes/default';
import { convertTimestampsToLocalizedTime } from '@/utils/timestampToLocalizedTime';

const markdownStyles = (theme: ChakraTheme) => css`
  & p {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  & > p:first-of-type {
    margin-top: 0;
  }

  & > p:last-of-type {
    margin-bottom: 0;
  }

  & img {
    max-width: 100%;
    height: auto;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 1em;
    margin-bottom: 1em;
  }

  h1 {
    font-size: 60px;
    line-height: calc(72 / 60);
  }

  h2 {
    font-size: 48px;
    line-height: calc(60 / 48);
  }

  h3 {
    font-size: 36px;
    line-height: calc(44 / 36);
  }

  h4 {
    font-size: 30px;
    line-height: calc(36 / 30);
  }

  blockquote p::before {
    content: open-quote;
  }
  blockquote p::after {
    content: close-quote;
  }
  blockquote p {
    quotes: '“' '”' '‘' '’';
  }

  ul,
  ol {
    padding-left: 40px;
    margin: 20px 0;

    & p {
      margin: 0;
    }
  }

  ul {
    list-style: disc;
  }

  & a {
    color: ${theme.legacy.colors.blueLink};
    text-decoration: none;
    word-break: break-word;

    &:visited {
      color: ${theme.legacy.colors.blueLink};
    }

    &:hover {
      text-decoration: underline;
    }

    img {
      display: inline-block;
    }
  }

  .videoIframeWrapper {
    overflow: hidden;
    padding-top: 56.25%;
    position: relative;
    margin: 40px 0;

    iframe {
      border: 0;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
`;

/**
 *
 * @deprecated Use one of the `ui/shared/markdowns` component instead.
 */
export const Markdown: FC<OriginalReactMarkdown.ReactMarkdownProps & { children?: ReactNode }> = ({
  source,
  ...restProps
}) => {
  // we are modifing source since we are using some custom markdown syntax for some elements (timestamps, ...)
  const modifiedSource = useMemo(() => convertTimestampsToLocalizedTime(source), [source]);

  return <OriginalReactMarkdown css={markdownStyles} source={modifiedSource} escapeHtml={false} {...restProps} />;
};

Markdown.defaultProps = {
  linkTarget: 'blank',
};
